import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./LandingPage.css";
import { isFreemiumEnabled } from "./utils/freemiumFlag";
import { submitContactEnquiry } from "./api";

const EMPTY_CONTACT = { name: "", email: "", phone: "", company: "", hiringFor: "", message: "", website: "" };

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const freemiumOn = isFreemiumEnabled();
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [contactStatus, setContactStatus] = useState({ state: "idle", message: "" });

  const updateContact = (field) => (e) => setContact((c) => ({ ...c, [field]: e.target.value }));

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus({ state: "sending", message: "" });
    try {
      await submitContactEnquiry(contact);
      setContact(EMPTY_CONTACT);
      setContactStatus({ state: "success", message: "Thanks! Our team will reach out within one business day." });
    } catch (err) {
      setContactStatus({ state: "error", message: err.message });
    }
  };

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartFree = () => {
    if (freemiumOn) {
      navigate('/login', { state: { returnTo: '/setup' } });
    } else {
      navigate('/login');
    }
  };

  const scrollToDemo = () => {
    const el = document.getElementById('demo-video');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to section based on URL hash (e.g. /#contact)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="landing-container">
      {/* Animated background */}
      <div className="background-animation">
        <div className="gradient-blob blob-1"></div>
        <div className="gradient-blob blob-2"></div>
        <div className="gradient-blob blob-3"></div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <div className="logo-section">
            <div className="logo-icon">
              <img src="/Vagmee_Logo.png" alt="Vagmee" className="nav-logo" />
            </div>
            <div className="logo-branding">
              <span className="logo-text"><b>Vagmee</b></span>
              <span className="logo-tagline">AI hiring, done in days.</span>
            </div>
          </div>
          <div className="nav-links">
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#features" className="nav-link">Features</a>
            <a href="#hiring-levels" className="nav-link">Roles</a>
            <a href="#practice-ready" className="nav-link">For Candidates</a>
            <a href="#contact" className="nav-link">Contact Us</a>
            <button onClick={() => navigate('/login')} className="nav-cta">Sign In</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Hire in <span className="gradient-text">2–3 Days</span>, Not 6 Weeks.
            </h1>
            <p className="hero-subtitle">
              Vagmee is an AI screening platform that runs your whole hiring pipeline, from application to shortlist. It works for interns, freshers and lateral hires.
            </p>

            {/* CTA Buttons */}
            <div className="cta-buttons">
              <button onClick={() => navigate('/login')} className="cta-primary">
                <span className="cta-icon">🏢</span>
                Start Hiring
              </button>
              <button onClick={scrollToContact} className="cta-secondary">
                <span className="cta-icon">📞</span>
                Talk to Us
              </button>
            </div>

            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">2–3 Days</span>
                <span className="stat-label">Job Post to Shortlist</span>
              </div>
              <div className="stat">
                <span className="stat-number">100+</span>
                <span className="stat-label">Job Roles Supported</span>
              </div>
              <div className="stat">
                <span className="stat-number">All Levels</span>
                <span className="stat-label">Intern to Senior</span>
              </div>
              <div className="stat">
                <span className="stat-number">24×7</span>
                <span className="stat-label">AI Interviews</span>
              </div>
            </div>
          </div>

          {/* 3D Hero Visual */}
          <div className="hero-visual">
            <div className="glowing-cloud">
              <svg viewBox="0 0 300 300" className="cloud-svg">
                {/* Glowing background circle */}
                <circle cx="150" cy="150" r="140" fill="none" stroke="url(#glowGradient)" strokeWidth="2" opacity="0.4" className="glow-ring" />

                {/* Cloud shape */}
                <path
                  d="M 80 150 Q 80 120 110 120 Q 120 100 150 100 Q 180 100 190 120 Q 220 120 220 150 Q 220 180 190 180 L 110 180 Q 80 180 80 150 Z"
                  fill="url(#cloudGradient)"
                  opacity="0.8"
                />

                {/* Neural nodes */}
                <circle cx="100" cy="130" r="6" fill="#ff00ff" opacity="0.9" className="neural-node" />
                <circle cx="150" cy="110" r="6" fill="#00ffff" opacity="0.9" className="neural-node" />
                <circle cx="200" cy="130" r="6" fill="#ff00ff" opacity="0.9" className="neural-node" />
                <circle cx="120" cy="170" r="6" fill="#00ffff" opacity="0.9" className="neural-node" />
                <circle cx="180" cy="170" r="6" fill="#ff00ff" opacity="0.9" className="neural-node" />

                {/* Connecting lines */}
                <line x1="100" y1="130" x2="150" y2="110" stroke="#00ffff" strokeWidth="1" opacity="0.5" className="connection-line" />
                <line x1="150" y1="110" x2="200" y2="130" stroke="#ff00ff" strokeWidth="1" opacity="0.5" className="connection-line" />
                <line x1="100" y1="130" x2="120" y2="170" stroke="#ff00ff" strokeWidth="1" opacity="0.5" className="connection-line" />
                <line x1="200" y1="130" x2="180" y2="170" stroke="#00ffff" strokeWidth="1" opacity="0.5" className="connection-line" />
                <line x1="120" y1="170" x2="180" y2="170" stroke="#ff00ff" strokeWidth="1" opacity="0.5" className="connection-line" />

                {/* Microphone icon */}
                <g className="microphone-icon">
                  <circle cx="150" cy="150" r="8" fill="none" stroke="#00ffff" strokeWidth="2" />
                  <path d="M 150 140 L 150 160" stroke="#00ffff" strokeWidth="2" />
                  <path d="M 145 160 L 155 160" stroke="#00ffff" strokeWidth="2" />
                </g>

                {/* Waveform */}
                <path d="M 70 150 Q 80 140 90 150 T 110 150" stroke="#ff00ff" strokeWidth="2" fill="none" opacity="0.6" className="waveform" />
                <path d="M 210 150 Q 220 140 230 150 T 250 150" stroke="#00ffff" strokeWidth="2" fill="none" opacity="0.6" className="waveform" />

                {/* Gradients */}
                <defs>
                  <linearGradient id="cloudGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff00ff" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#00ffff" stopOpacity="0.8" />
                  </linearGradient>
                  <radialGradient id="glowGradient">
                    <stop offset="0%" stopColor="#ff00ff" />
                    <stop offset="100%" stopColor="#00ffff" />
                  </radialGradient>
                </defs>
              </svg>

              {/* Floating particles */}
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
              <div className="particle particle-4"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Hiring Pipeline */}
      <section id="how-it-works" className="how-it-works">
        <h2 className="section-title">Your Complete Hiring Pipeline, Automated</h2>

        <div className="flow-steps">
          <div className="step glass-card">
            <div className="step-number">1</div>
            <h3 className="step-title">Post the Role</h3>
            <p className="step-description">Add the job description, or upload your own question set</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">2</div>
            <h3 className="step-title">Share One Link</h3>
            <p className="step-description">Add the public link to your job ads, and candidates sign up themselves</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">3</div>
            <h3 className="step-title">AI Screens Everyone</h3>
            <p className="step-description">MCQ, coding and AI voice interviews run for every applicant, 24×7</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">4</div>
            <h3 className="step-title">Proctored &amp; Recorded</h3>
            <p className="step-description">Every session is video-recorded and monitored for integrity</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">5</div>
            <h3 className="step-title">Scored Reports</h3>
            <p className="step-description">A PDF report and integrity score for every candidate</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">6</div>
            <h3 className="step-title">Interview the Best</h3>
            <p className="step-description">Spend your time only on a ranked shortlist, within 2–3 days</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features">
        <div className="features-header">
          <h2 className="section-title">Everything You Need to Screen at Scale</h2>
          <p className="section-subtitle">One platform replaces manual first rounds, phone screens and take-home tests</p>
        </div>

        <div className="features-grid">
          <div className="feature-card glass-card">
            <div className="feature-icon">🎙️</div>
            <h3 className="feature-title">AI Voice Interviews</h3>
            <p className="feature-description">
              Real-time spoken interviews that ask follow-up questions and adapt to each candidate's answers.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">📚</div>
            <h3 className="feature-title">Your Question Bank</h3>
            <p className="feature-description">
              Bring your own questions. The AI varies them for each candidate, so answers can't be shared, while staying on your topics.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">💻</div>
            <h3 className="feature-title">Coding &amp; MCQ Tests</h3>
            <p className="feature-description">
              Auto-graded coding problems and aptitude or technical MCQs, part of the same pipeline.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">📹</div>
            <h3 className="feature-title">Video Recording &amp; Proctoring</h3>
            <p className="feature-description">
              Every session is recorded and stored, so you can review any candidate's interview later.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">🛡️</div>
            <h3 className="feature-title">Integrity Score</h3>
            <p className="feature-description">
              Tab switches, multiple faces and suspicious behaviour are flagged and scored automatically.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">📄</div>
            <h3 className="feature-title">PDF Candidate Reports</h3>
            <p className="feature-description">
              A detailed, shareable report for each candidate, ready for your hiring managers.
            </p>
            <div className="feature-accent"></div>
          </div>
        </div>
      </section>

      {/* Hiring Levels */}
      <section id="hiring-levels" className="features">
        <div className="features-header">
          <h2 className="section-title">Built for Every Hiring Level</h2>
          <p className="section-subtitle">From campus interns to experienced lateral hires, in one pipeline</p>
        </div>

        <div className="features-grid">
          <div className="feature-card glass-card">
            <div className="feature-icon">🎓</div>
            <h3 className="feature-title">Interns &amp; Campus</h3>
            <p className="feature-description">
              Screen hundreds of students on fundamentals, aptitude and communication without running campus rounds.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">🚀</div>
            <h3 className="feature-title">Freshers</h3>
            <p className="feature-description">
              Check real coding ability and role readiness before anyone on your team spends time on a call.
            </p>
            <div className="feature-accent"></div>
          </div>

          <div className="feature-card glass-card">
            <div className="feature-icon">💼</div>
            <h3 className="feature-title">Lateral &amp; Experienced</h3>
            <p className="feature-description">
              Questions that match the candidate's experience, going deep on system design, domain knowledge and past projects.
            </p>
            <div className="feature-accent"></div>
          </div>
        </div>
      </section>

      {/* Employer Section */}
      <section id="employers" className="how-it-works">
        <div className="features-header">
          <h2 className="section-title">Why Teams Switch to Vagmee</h2>
          <p className="section-subtitle">
            Stop spending weeks on first-round screening. Let AI do it, and make decisions with evidence.
          </p>
        </div>

        <ul className="employer-benefits">
          <li>No manual first rounds</li>
          <li>Same rubric for every candidate</li>
          <li>Catch cheating and proxies</li>
          <li>Shortlist in 2–3 days</li>
          <li>One link that plugs into your job ads</li>
          <li>Recordings and reports for every hire</li>
        </ul>

        <div className="cta-buttons" style={{ justifyContent: 'center' }}>
          <button onClick={() => navigate('/login')} className="cta-primary">
            <span className="cta-icon">🏢</span>
            Start Hiring
          </button>
          <button onClick={scrollToContact} className="cta-secondary">
            <span className="cta-icon">📞</span>
            Talk to Us
          </button>
        </div>
      </section>

      {/* Contact Us */}
      <section id="contact" className="features">
        <div className="features-header">
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">
            Tell us about your hiring needs, and our team will get back to you within one business day.
          </p>
        </div>

        <form className="contact-form glass-card" onSubmit={handleContactSubmit}>
          <div className="contact-form-grid">
            <div className="contact-field">
              <label htmlFor="contact-name">Full Name *</label>
              <input id="contact-name" type="text" required maxLength={120} autoComplete="name"
                value={contact.name} onChange={updateContact('name')} />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-company">Company</label>
              <input id="contact-company" type="text" maxLength={120} autoComplete="organization"
                value={contact.company} onChange={updateContact('company')} />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-email">Work Email *</label>
              <input id="contact-email" type="email" required maxLength={120} autoComplete="email"
                value={contact.email} onChange={updateContact('email')} />
            </div>
            <div className="contact-field">
              <label htmlFor="contact-phone">Phone Number *</label>
              <input id="contact-phone" type="tel" required maxLength={20} autoComplete="tel"
                pattern="[0-9+()\-. ]{10,20}" placeholder="+91 98765 43210"
                value={contact.phone} onChange={updateContact('phone')} />
            </div>
            <div className="contact-field full">
              <label htmlFor="contact-hiring">Hiring For</label>
              <select id="contact-hiring" value={contact.hiringFor} onChange={updateContact('hiringFor')}>
                <option value="">Select…</option>
                <option value="intern">Interns / Campus</option>
                <option value="fresher">Freshers</option>
                <option value="lateral">Lateral / Experienced</option>
                <option value="mixed">A mix of these</option>
              </select>
            </div>
            <div className="contact-field full">
              <label htmlFor="contact-message">Message</label>
              <textarea id="contact-message" maxLength={2000}
                placeholder="Roles, number of openings, timelines…"
                value={contact.message} onChange={updateContact('message')} />
            </div>
            {/* Honeypot: hidden from real users, bots fill it in */}
            <div className="contact-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input id="contact-website" type="text" tabIndex={-1} autoComplete="off"
                value={contact.website} onChange={updateContact('website')} />
            </div>
          </div>

          <div className="contact-form-actions">
            <button type="submit" className="cta-primary" disabled={contactStatus.state === 'sending'}>
              <span className="cta-icon">📨</span>
              {contactStatus.state === 'sending' ? 'Sending…' : 'Request a Callback'}
            </button>
          </div>
          {contactStatus.message && (
            <p className={`contact-status ${contactStatus.state}`} role="status">{contactStatus.message}</p>
          )}
        </form>
      </section>

      {/* ─────────── For Candidates ─────────── */}
      <section id="practice-ready" className="features">
        <div className="features-header">
          <h2 className="section-title">For Candidates: Get Interview Ready</h2>
          <p className="section-subtitle">
            Looking for a job? Practice with AI-powered interviews, improve your communication, and find out where you can improve before you face a real employer.
          </p>
        </div>

        <div className="cta-buttons">
          <button onClick={handleStartFree} className="cta-primary">
            <span className="cta-icon">🎤</span>
            Practice an Interview
          </button>
          <button onClick={scrollToDemo} className="cta-secondary">
            <span className="cta-icon">▶️</span>
            Watch Demo
          </button>
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo-video" className="demo-video-section">
        <div className="demo-video-container">
          <div className="demo-video-header">
            <h2 className="section-title">See Vagmee in Action</h2>
            <p className="section-subtitle">Watch how our AI-powered interview platform helps you practice and improve</p>
          </div>
          <div className="video-wrapper">
            <video
              className="demo-video"
              controls
              preload="auto"
            >
              <source src="/demo_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Practice How It Works */}
      <section id="practice-how-it-works" className="how-it-works">
        <h2 className="section-title">How Practice Works</h2>

        <div className="steps-container">
          <div className="step glass-card">
            <div className="step-number">1</div>
            <h3 className="step-title">Setup Your Profile</h3>
            <p className="step-description">Enter your target role, experience level, and difficulty preference</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">2</div>
            <h3 className="step-title">Practice Interview</h3>
            <p className="step-description">Answer AI-generated questions in real-time with speech recognition</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">3</div>
            <h3 className="step-title">Get Insights</h3>
            <p className="step-description">Receive detailed performance metrics and personalized improvement tips</p>
          </div>

          <div className="step-arrow">→</div>

          <div className="step glass-card">
            <div className="step-number">4</div>
            <h3 className="step-title">Improve & Repeat</h3>
            <p className="step-description">Practice unlimited sessions and track your progress over time</p>
          </div>
        </div>
      </section>

      {freemiumOn && (
        <>
          {/* Testimonials (placeholder) */}
          <section id="testimonials" className="features">
            <div className="features-header">
              <h2 className="section-title">What Candidates Say</h2>
              <p className="section-subtitle">Real stories from students who practiced with Vagmee</p>
            </div>
            <div className="features-grid">
              <div className="feature-card glass-card">
                <div className="feature-icon">💬</div>
                <p className="feature-description">"Vagmee's AI interview felt like the real thing — I walked into my actual interview far more confident."</p>
                <h3 className="feature-title">— Placement Candidate</h3>
              </div>
              <div className="feature-card glass-card">
                <div className="feature-icon">💬</div>
                <p className="feature-description">"The detailed report showed me exactly what to fix before my next interview."</p>
                <h3 className="feature-title">— Placement Candidate</h3>
              </div>
              <div className="feature-card glass-card">
                <div className="feature-icon">💬</div>
                <p className="feature-description">"Coding practice plus mock interviews in one place made prep so much easier."</p>
                <h3 className="feature-title">— Placement Candidate</h3>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="how-it-works">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="features-grid">
              <div className="feature-card glass-card">
                <h3 className="feature-title">Is the first interview really free?</h3>
                <p className="feature-description">Yes — every new student account includes one full AI interview with a real report preview, no card required.</p>
              </div>
              <div className="feature-card glass-card">
                <h3 className="feature-title">What do I get after upgrading?</h3>
                <p className="feature-description">Unlimited AI interviews (per your plan), full coding and MCQ practice, complete detailed reports, and your placement readiness dashboard.</p>
              </div>
              <div className="feature-card glass-card">
                <h3 className="feature-title">Is payment secure?</h3>
                <p className="feature-description">All payments are processed securely through Razorpay with UPI, cards, netbanking, and wallets supported.</p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* CTA Section */}
      <section className="final-cta">
        <div className="cta-content glass-card">
          <h2 className="cta-title">Fill Your Next Role This Week</h2>
          <p className="cta-text">
            Post a role today and have a scored, proctored shortlist in 2–3 days, whether you're hiring interns or experienced engineers.
          </p>
          <div className="cta-buttons" style={{ justifyContent: 'center' }}>
            <button onClick={() => navigate('/login')} className="cta-primary large">
              Start Hiring
            </button>
            <button onClick={scrollToContact} className="cta-secondary">
              Talk to Us
            </button>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <div className="landing-trust-strip">
        <span className="landing-trust-item">🔒 Secured by <strong>Razorpay</strong></span>
        <span className="landing-trust-sep">·</span>
        <span className="landing-trust-item">💳 UPI · Cards · Netbanking · Wallets</span>
        <span className="landing-trust-sep">·</span>
        <span className="landing-trust-item">🛡️ 256-bit SSL Encryption</span>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Vagmee</h4>
            <p>AI hiring, done in days.</p>
          </div>
          <div className="footer-section">
            <h5>Product</h5>
            <ul>
              <li><a href="#how-it-works">How It Works</a></li>
              <li><a href="#features">Features</a></li>
              <li><a href="#hiring-levels">Hiring Levels</a></li>
              <li><a href="#practice-ready">For Candidates</a></li>
              <li><a href="#contact">Contact Us</a></li>
              <li><button onClick={() => navigate('/pricing')} className="footer-link-button">Pricing</button></li>
            </ul>
          </div>
          <div className="footer-section">
            <h5>Company</h5>
            <ul>
                <li><button onClick={() => navigate('/about#mission-section')} className="footer-link-button">About</button></li>
                <li><button onClick={() => navigate('/about#contact-section')} className="footer-link-button">Contact</button></li>
                <li><button onClick={() => navigate('/about#policies-section')} className="footer-link-button">Policies</button></li>
            </ul>
        </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 Vagmee. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
